import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  loginUser,
  createUser,
  signOut,
  checkAuth,
  resetPasswordRequest,
  resetPassword,
  sendOTP,
  verifyOTP,
  createUserWithOTP,
  sendPasswordResetOTP,
  verifyPasswordResetOTP,
  resetPasswordWithOTP,
} from './authAPI';
import { updateUser } from '../user/userAPI';

const initialState = {
  loggedInUserToken: null, // this should only contain user identity => 'id'/'role'
  status: 'idle',
  error: null,
  userChecked: false,
  mailSent: false,
  passwordReset: false,
  otpSent: false,
  otpVerified: false,
  tempToken: null,
  registrationEmail: null,
  passwordResetOtpSent: false,
  passwordResetOtpVerified: false,
  passwordResetTempToken: null,
};

export const createUserAsync = createAsyncThunk(
  'user/createUser',
  async (userData) => {
    const response = await createUser(userData);
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);

export const loginUserAsync = createAsyncThunk(
  'user/loginUser',
  async (loginInfo, { rejectWithValue }) => {
    try {
      const response = await loginUser(loginInfo);
      return response.data;
    } catch (error) {      return rejectWithValue(
        typeof error === 'string'
          ? error
          : error?.message || 'Invalid email or password'
      );
    }
  }
);

export const checkAuthAsync = createAsyncThunk('user/checkAuth', async () => {
  try {
    const response = await checkAuth();
    return response.data;
  } catch (error) {  }
});
export const resetPasswordRequestAsync = createAsyncThunk(
  'user/resetPasswordRequest',
  async (email, { rejectWithValue }) => {
    try {
      const response = await resetPasswordRequest(email);
      return response.data;
    } catch (error) {      return rejectWithValue(
        typeof error === 'string'
          ? error
          : error?.message || 'Password reset request failed'
      );
    }
  }
);

export const resetPasswordAsync = createAsyncThunk(
  'user/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await resetPassword(data);      return response.data;
    } catch (error) {      return rejectWithValue(
        typeof error === 'string'
          ? error
          : error?.message || 'Password reset failed'
      );
    }
  }
);

export const signOutAsync = createAsyncThunk(
  'user/signOut',
  async () => {
    const response = await signOut();
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);

export const sendOTPAsync = createAsyncThunk(
  'user/sendOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await sendOTP(email);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyOTPAsync = createAsyncThunk(
  'user/verifyOTP',
  async (data, { rejectWithValue }) => {
    try {
      const response = await verifyOTP(data);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createUserWithOTPAsync = createAsyncThunk(
  'user/createUserWithOTP',
  async (data, { rejectWithValue }) => {
    try {
      const response = await createUserWithOTP(data);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendPasswordResetOTPAsync = createAsyncThunk(
  'user/sendPasswordResetOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await sendPasswordResetOTP(email);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyPasswordResetOTPAsync = createAsyncThunk(
  'user/verifyPasswordResetOTP',
  async (data, { rejectWithValue }) => {
    try {
      const response = await verifyPasswordResetOTP(data);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const resetPasswordWithOTPAsync = createAsyncThunk(
  'user/resetPasswordWithOTP',
  async (data, { rejectWithValue }) => {
    try {
      const response = await resetPasswordWithOTP(data);
      return response.data;
    } catch (error) {      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const authSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createUserAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUserToken = action.payload;
        // Save token to localStorage if present in response
        if (action.payload?.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(loginUserAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loginUserAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUserToken = action.payload;
        // Save token to localStorage if present in response
        if (action.payload?.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(loginUserAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(signOutAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(signOutAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUserToken = null;
        // Clear token from localStorage on logout
        localStorage.removeItem('token');
      })
      .addCase(checkAuthAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkAuthAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUserToken = action.payload;
        state.userChecked = true;
        // Save token to localStorage if present in response
        if (action.payload?.token) {
          localStorage.setItem('token', action.payload.token);
        }
      })
      .addCase(checkAuthAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.userChecked = true;
      })
      .addCase(resetPasswordRequestAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resetPasswordRequestAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.mailSent = true;
      })
      .addCase(resetPasswordAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resetPasswordAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.passwordReset = true;
      })
      .addCase(resetPasswordAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload
      })
      .addCase(sendOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.otpSent = true;
        state.error = null;
      })
      .addCase(sendOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(verifyOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(verifyOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.otpVerified = true;
        state.tempToken = action.payload.tempToken;
        state.registrationEmail = action.payload.email;
        state.error = null;
      })
      .addCase(verifyOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(createUserWithOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createUserWithOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUserToken = action.payload;
        state.otpSent = false;
        state.otpVerified = false;
        state.tempToken = null;
        state.registrationEmail = null;
      })
      .addCase(createUserWithOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(sendPasswordResetOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendPasswordResetOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.passwordResetOtpSent = true;
        state.error = null;
      })
      .addCase(sendPasswordResetOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(verifyPasswordResetOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(verifyPasswordResetOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.passwordResetOtpVerified = true;
        state.passwordResetTempToken = action.payload.tempToken;
        state.error = null;
      })
      .addCase(verifyPasswordResetOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      .addCase(resetPasswordWithOTPAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resetPasswordWithOTPAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.passwordReset = true;
        state.passwordResetOtpSent = false;
        state.passwordResetOtpVerified = false;
        state.passwordResetTempToken = null;
        state.error = null;
      })
      .addCase(resetPasswordWithOTPAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
  },
});

export const selectLoggedInUser = (state) => state.auth.loggedInUserToken;
export const selectError = (state) => state.auth.error;
export const selectUserChecked = (state) => state.auth.userChecked;
export const selectMailSent = (state) => state.auth.mailSent;
export const selectPasswordReset = (state) => state.auth.passwordReset;
export const selectOtpSent = (state) => state.auth.otpSent;
export const selectOtpVerified = (state) => state.auth.otpVerified;
export const selectTempToken = (state) => state.auth.tempToken;
export const selectRegistrationEmail = (state) => state.auth.registrationEmail;
export const selectPasswordResetOtpSent = (state) => state.auth.passwordResetOtpSent;
export const selectPasswordResetOtpVerified = (state) => state.auth.passwordResetOtpVerified;
export const selectPasswordResetTempToken = (state) => state.auth.passwordResetTempToken;

export const { clearError } = authSlice.actions;

export default authSlice.reducer;
